// pattern: Imperative Shell

type MessageState = 'success'|'error'|'pending'

type TurnstileGlobal = {
    reset:(widgetId?:string)=>void;
}

declare global {
    interface Window {
        turnstile?:TurnstileGlobal;
    }
}

const ERROR_TEXT_INVALID_EMAIL =
    'Please enter a valid email address.'
const ERROR_TEXT_OPAQUE =
    'Something went wrong. Please try again.'
const ERROR_TEXT_NETWORK =
    'We couldn\'t reach our servers. Please try again.'
const SUCCESS_TEXT = 'Thanks! We\'ll be in touch.'

function showMessage (
    el:HTMLElement,
    text:string,
    state:MessageState,
):void {
    el.textContent = text
    el.setAttribute('data-state', state)
}

function resetTurnstile ():void {
    const t = window.turnstile
    if (t && typeof t.reset === 'function') {
        t.reset()
    }
}

async function handleSubmit (
    form:HTMLFormElement,
    event:SubmitEvent,
):Promise<void> {
    event.preventDefault()

    const submit = form.querySelector<HTMLButtonElement>(
        'button[type="submit"]',
    )
    const message = form.querySelector<HTMLElement>(
        '[data-signup-message]',
    )
    if (!submit || !message) return

    submit.disabled = true
    // Clear any stale terminal text so the live region holds nothing
    // in-flight (no premature or duplicate announcement) and the
    // reserved slot stays visually empty until the outcome lands.
    message.textContent = ''
    message.removeAttribute('data-state')

    const action = form.getAttribute('action') || '/api/signup'

    let res:Response
    try {
        res = await fetch(action, {
            method: 'POST',
            body: new FormData(form),
        })
    } catch (_err) {
        showMessage(message, ERROR_TEXT_NETWORK, 'error')
        submit.disabled = false
        resetTurnstile()
        return
    }

    if (res.status === 200) {
        // Reveal in place: do not move the message or hide the form,
        // and leave submit disabled so the slot is the only change.
        showMessage(message, SUCCESS_TEXT, 'success')
        return
    }

    // 4xx / 5xx — re-enable submit and surface inline copy. A Turnstile
    // token is single-use and time-limited, so reset the widget on every
    // failed attempt: any retry must carry a fresh token, or siteverify
    // rejects the spent one with a 403 that masks the original error.
    submit.disabled = false
    resetTurnstile()

    let body:{ error?:string } = {}
    try {
        body = await res.json() as { error?:string }
    } catch (_err) {
        body = {}
    }

    if (res.status === 400 && body.error === 'invalid-email') {
        showMessage(message, ERROR_TEXT_INVALID_EMAIL, 'error')
        return
    }

    if (
        (res.status === 400 && body.error === 'invalid') ||
        res.status === 403
    ) {
        showMessage(message, ERROR_TEXT_OPAQUE, 'error')
        return
    }

    // 500 or any other status — generic network message.
    showMessage(message, ERROR_TEXT_NETWORK, 'error')
}

function init ():void {
    const form = document.querySelector<HTMLFormElement>(
        'form[data-signup]',
    )
    if (!form) return
    form.addEventListener('submit', (event) => {
        handleSubmit(form, event as SubmitEvent).catch(() => {
            // handleSubmit handles its own errors; this is a
            // belt-and-braces guard so an unexpected throw
            // doesn't surface as an uncaught promise rejection.
        })
    })
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
} else {
    init()
}

export { handleSubmit }
